import { Bar } from "react-chartjs-2";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Tooltip,
  Legend,
} from "chart.js";
import { useState, useEffect, useMemo } from "react";
import axios from "axios";
import { toast } from "react-toastify";
import { adminDashboardSellingUrl } from "../../assets/api/BaseURL";
import { getDisplayCurrency } from "../../../utils/currencyUtils";

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Tooltip,
  Legend
);

const SalesGraphDashboard = ({ token }) => {
  // ✅ Default = Last 30 Days
  const [range, setRange] = useState("last_30_days");
  const [dashboardData, setDashboardData] = useState(null);
  const [isLoading, setIsLoading] = useState(false);

  /* 📡 Fetch Dashboard Data */
  useEffect(() => {
    if (!token) return;

    const getData = async () => {
      setIsLoading(true);
      try {
        const response = await axios.get(
          `${adminDashboardSellingUrl}?range=${range}`,
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          }
        );

        if (response.status === 200 && response.data?.data) {
          setDashboardData(response.data.data);
        } else {
          setDashboardData(null);
          toast.info("No data found");
        }
      } catch (error) {
        setDashboardData(null);
        toast.error(
          error?.response?.data?.message || error.message
        );
      } finally {
        setIsLoading(false);
      }
    };

    getData();
  }, [token, range]);

  /* 📊 Chart Data */
  const chartData = useMemo(() => {
    if (!dashboardData?.daily_sales) {
      return { labels: [], datasets: [] };
    }

    return {
      labels: dashboardData.daily_sales.map((item) =>
        new Date(item.order_date).toLocaleDateString("en-IN", {
          day: "2-digit",
          month: "short",
        })
      ),
      datasets: [
        {
          label: `Revenue (${getDisplayCurrency(
            localStorage.getItem("currencySymbol"),
            localStorage.getItem("currencyCode")
          )})`,
          data: dashboardData.daily_sales.map((item) =>
            Number(item.total_sum)
          ),
          backgroundColor: "#4B49AC",
        },
      ],
    };
  }, [dashboardData]);

  /* ❌ No Data Condition */
  const isNoData =
    !dashboardData ||
    !dashboardData.daily_sales ||
    dashboardData.daily_sales.length === 0;

  return (
    <div className="bg-white">

      {/* 🔷 Header */}
      <div className="d-flex justify-content-between align-items-center mx-3 my-3">
        <h4 className="mb-0">  Revenue Overview</h4>

        {/* ✅ Updated Filters */}
        <select
          className="form-select form-select-sm w-auto"
          value={range}
          onChange={(e) => setRange(e.target.value)}
        >
          <option value="today">Today</option>
          <option value="yesterday">Yesterday</option>
          <option value="last_7_days">Last 7 Days</option>
          <option value="last_30_days">Last 30 Days</option>
          <option value="this_month">This Month</option>
          <option value="last_month">Last Month</option>
          <option value="last_90_days">Last 90 Days</option>
        </select>
      </div>

      {/* 📊 Chart / No Data */}
      <div className="card-body text-center">
        {isLoading ? (
          <p className="mb-0">Loading...</p>
        ) : isNoData ? (
          <div>
            <p className="mb-1 text-muted">No Data Available</p>
            <small className="text-muted">
              No sales data found for selected range
            </small>
          </div>
        ) : (
          <Bar
            data={chartData}
            height={70}
            options={{
              responsive: true,
              plugins: {
                legend: {
                  display: true,
                  position: "top",
                },
              },
              scales: {
                y: {
                  beginAtZero: true,
                },
                x: {
                  ticks: {
                    maxRotation: 45,
                    minRotation: 45,
                  },
                },
              },
            }}
          />
        )}
      </div>
    </div>
  );
};

export default SalesGraphDashboard;